import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { mainSiteUrl, categories } from '@/mocks/categories';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setShowCategories(false);
  }, [location]);

  const isHome = location.pathname === '/';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-14 md:h-16">
          <Link
            to="/"
            className={`font-heading text-lg md:text-xl font-bold transition-colors duration-200 ${
              isScrolled ? 'text-foreground-900' : 'text-white'
            }`}
          >
            BaseTao Spreadsheet Guide
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`whitespace-nowrap px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                isHome
                  ? (isScrolled ? 'text-primary-600' : 'text-white/90')
                  : (isScrolled ? 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100' : 'text-white/80 hover:text-white hover:bg-white/10')
              }`}
            >
              Home
            </Link>
            <Link
              to="/guides"
              className={`whitespace-nowrap px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                isScrolled ? 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100' : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              Shopping Guide
            </Link>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className={`whitespace-nowrap px-4 py-2 rounded-lg text-sm font-semibold transition-colors duration-200 ${
                isScrolled
                  ? 'bg-primary-500 text-white hover:bg-primary-600'
                  : 'bg-white/90 text-primary-700 hover:bg-white'
              }`}
            >
              Buy Now
            </a>
          </nav>

          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-md transition-colors duration-200 ${
              isScrolled ? 'text-foreground-800' : 'text-white'
            }`}
            aria-label="Toggle menu"
          >
            <i className={`ri-${isOpen ? 'close' : 'menu'}-line w-5 h-5 flex items-center justify-center`}></i>
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="md:hidden bg-white border-b border-background-200/70 shadow-lg max-h-[80vh] overflow-y-auto">
          <nav className="max-w-6xl mx-auto px-4 py-3 flex flex-col gap-1">
            <Link
              to="/"
              className="px-3 py-2.5 rounded-md text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors duration-200"
            >
              <i className="ri-home-4-line w-4 h-4 inline-flex items-center justify-center mr-2 text-primary-500"></i>
              Home
            </Link>
            <Link
              to="/guides"
              className="px-3 py-2.5 rounded-md text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors duration-200"
            >
              <i className="ri-book-open-line w-4 h-4 inline-flex items-center justify-center mr-2 text-primary-500"></i>
              Shopping Guide
            </Link>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-3 py-2.5 rounded-md text-sm font-semibold text-primary-600 hover:bg-primary-50 transition-colors duration-200"
            >
              <i className="ri-shopping-bag-3-line w-4 h-4 inline-flex items-center justify-center mr-2"></i>
              Buy Now
            </a>

            <div className="border-t border-background-200/70 my-2"></div>

            <button
              onClick={() => setShowCategories(!showCategories)}
              className="px-3 py-2.5 rounded-md text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors duration-200 flex items-center justify-between w-full"
            >
              <span>
                <i className="ri-grid-line w-4 h-4 inline-flex items-center justify-center mr-2 text-primary-500"></i>
                Categories
              </span>
              {showCategories ? (
                <i className="ri-arrow-up-s-line w-4 h-4 flex items-center justify-center text-foreground-500 transition-transform duration-200"></i>
              ) : (
                <i className="ri-arrow-down-s-line w-4 h-4 flex items-center justify-center text-foreground-500 transition-transform duration-200"></i>
              )}
            </button>

            {showCategories && (
              <div className="pl-5 flex flex-col gap-0.5">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="px-3 py-2 rounded-md text-sm text-foreground-700 hover:text-primary-600 hover:bg-background-100 transition-colors duration-200 flex items-center"
                  >
                    <i className="ri-arrow-right-up-line w-3.5 h-3.5 inline-flex items-center justify-center mr-2 text-foreground-400"></i>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}